import { useEffect, useMemo, useState } from 'react';
import classes from 'components/widgets/phasePerformanceBreakdown/phasePerformanceBreakdown.module.scss';
import {
    getFiltered,
    IPhasePerformanceBreakdownCsvData,
    isProjectExpected,
    phasePerformanceBreakdownCsvHeaders,
    prepareToCsv,
} from 'components/Dashboards/Portfolio/phasePerformance/phasePerformance.utils';
import { ProjectRow } from 'components/widgets/phasePerformanceBreakdown/components/projectRow/projectRow';
import { Header } from 'components/widgets/phasePerformanceBreakdown/components/header/header';
import produce from 'immer';
import { LoaderContainer } from 'components/common';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import NoData from 'components/common/NoData/noData';
import { ErrorRow } from 'components/widgets/phasePerformanceBreakdown/components/errorRow/errorRow';
import {
    IPhasePerformanceContracts,
    IPhasePerformanceProject,
    IProjectStatus,
} from 'components/Dashboards/Portfolio/phasePerformance/phasePerformance.types';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { prepareVersionSelectTitle } from 'components/common/TreeProgramFilter/VersionSelect/versionSelect.utils';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import { getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { getStringWithoutHash } from 'utilitys/helpers/general';
import { usePhasePerformanceStore } from 'store/phasePerformance.store';
import { useQueryPhasePerformanceGetContracts } from 'api/queries/getMappedContracts.query';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';

export interface Props extends IComponentProps {
    widgetId?: string;
    localFilters?: {
        templateId: number;
        selectedProject: IPhasePerformanceProject[];
        selectedStatus: ISelectOption<IProjectStatus>[];
        selectedGates: ISelectOption<number> | null;
        selectedTags: ISelectOption<number>[];
        templateName: string;
    };
}

export const PhasePerformanceBreakdown = ({
    widgetId,
    localFilters,
    editNarrative = null,
    externalComponents = null,
    setSubTitleForExport,
}: Props) => {
    const { template, selectedProjects, selectedStatus, selectedGates, selectedTags } = usePhasePerformanceStore(
        (store) => ({
            template: store.template,
            selectedProjects: store.selectedProjects,
            selectedStatus: store.selectedStatus,
            selectedGates: store.selectedGates,
            selectedTags: store.selectedTags,
        }),
    );
    const templateId = template?.value;
    const templateName = template?.label;

    const filters = useMemo(
        () =>
            getFiltersData({
                CONTRACT_PHASE_PERFORMANCE:
                    widgetId && localFilters
                        ? localFilters.selectedProject?.map((item) => item.contract_id)
                        : selectedProjects?.map((item) => item.contract_id),
                STATUS_PHASE_PERFORMANCE:
                    widgetId && localFilters
                        ? localFilters.selectedStatus?.map((item) => item.value)
                        : selectedStatus?.map((item) => item.value),
                TAG_PHASE_PERFORMANCE:
                    widgetId && localFilters
                        ? localFilters.selectedTags?.map((item) => item.value)
                        : selectedTags?.map((item) => item.value),
            }),

        [widgetId, localFilters, selectedProjects, selectedStatus, selectedTags],
    );
    const [dataToCsv, setDataToCsv] = useState<IPhasePerformanceBreakdownCsvData[]>([]);
    const { data: contractsData, isLoading } = useQueryPhasePerformanceGetContracts({
        templateId: widgetId && localFilters ? localFilters.templateId : templateId,
        filter: {
            filters,
        },
    });
    const [filtered, setFiltered] = useState<IPhasePerformanceContracts[]>([]);

    const { generateFilters: formattedFilter } = useGlobalFiltersHook({
        widgetId,
        filter: {
            filters: [
                { name: 'Projects', data: localFilters?.selectedProject?.map((item) => item.name) },
                { name: 'Phase', data: localFilters?.selectedGates?.label ? [localFilters?.selectedGates?.label] : [] },
                { name: 'Status', data: localFilters?.selectedStatus?.map((item) => item.label) },
                { name: 'Tags', data: localFilters?.selectedTags?.map((item) => item.label) },
            ],
        },
    });

    // Prepare Data To CSV
    useEffect(() => {
        if (contractsData && setDataToCsv) {
            const dataToCsv = prepareToCsv({
                data: contractsData,
                selectedProject: localFilters?.selectedProject || selectedProjects,
            });
            setDataToCsv(dataToCsv);
        }
    }, [contractsData, selectedProjects, localFilters]);

    useEffect(() => {
        if (contractsData) {
            setFiltered(
                getFiltered({
                    contracts: contractsData.mapped_contracts,
                    selectedGates: localFilters?.selectedGates || selectedGates,
                }).filter((item) => !isProjectExpected(item)),
            );
        }
    }, [contractsData, selectedProjects, selectedStatus, selectedGates, localFilters]);

    const toggleExpandRow = (index: number): void => {
        setFiltered(
            produce(filtered, (draft) => {
                draft[index].isExpand = !draft[index].isExpand;
                return draft;
            }),
        );
    };

    const toggleExpandAll = (): void => {
        setFiltered(
            produce(filtered, (draft) => {
                const toggle = draft[0].isExpand;
                draft.forEach((item) => (item.isExpand = !toggle));
                return draft;
            }),
        );
    };

    const isExpandAll: boolean = filtered.every((item) => item.isExpand);
    const id = 'phasePerformanceBreakdown';
    const route = `${getStringWithoutHash(window.location.href)}#${id}`;
    const title = `Phase Performance Breakdown for template ${localFilters?.templateName || templateName}`;
    const subTitle: string = prepareVersionSelectTitle();

    const tableInstance = {
        visibleColumns: phasePerformanceBreakdownCsvHeaders,
        sortedRows: dataToCsv.map((item) => ({
            values: item,
        })),
    };

    useEffect(() => {
        setSubTitleForExport && setSubTitleForExport({ widgetId, subTitle, title });
    }, [widgetId, subTitle]);

    return (
        <WidgetWithTitle
            title={title}
            tooltip={contractsData?.phase_performance_breakdown_info}
            id={id}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={'phasePerformanceBreakdown'}
                    title={title}
                    widgetId={widgetId}
                    route={route}
                    localFilters={{
                        templateId,
                        selectedProjects,
                        selectedStatus,
                        selectedGates,
                        selectedTags,
                        templateName,
                    }}
                />,
                <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                <ExportDropdown
                    data={dataToCsv}
                    tableInstance={tableInstance}
                    key={'export'}
                    title={title}
                    subTitle={subTitle}
                />,
            ]}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && formattedFilter.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={formattedFilter} />
                ) : null,
            ]}
        >
            <>
                <div className={classes.wrapper}>
                    <div className={classes.container}>
                        {isLoading && (
                            <LoaderContainer height={'500px'}>
                                <OverlayWithSpinner />
                            </LoaderContainer>
                        )}
                        {!isLoading && filtered.length === 0 && <NoData />}
                        {!isLoading && filtered.length > 0 && (
                            <>
                                <Header toggleExpandAll={toggleExpandAll} isExpandAll={isExpandAll} />
                                <div className={classes.rows}>
                                    {filtered.map((row, index) => {
                                        return Boolean(row.error) ? (
                                            <ErrorRow key={row.contract_id + index} row={row} templateId={templateId} />
                                        ) : (
                                            <ProjectRow
                                                key={row.contract_id + index}
                                                toggleExpandRow={toggleExpandRow}
                                                index={index}
                                                row={row}
                                            />
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </>
        </WidgetWithTitle>
    );
};
